import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { GraduationCap, Lightbulb, TrendingUp, RefreshCw, Banknote, Ban, ArrowRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { BourseApplication } from "@/components/BourseApplication";
import { getCampagneBourse } from "@/lib/api";

const criteresPersonnes = [
  "Être une personne entrepreneure ou porteuse d'un projet entrepreneurial, représenter un organisme à but non lucratif admissible ou une société par actions en démarrage ayant besoin d'aide financière",
  "Résider au Canada ou y exercer principalement ses activités, sous réserve des critères particuliers de chaque programme de bourse",
  "Démontrer un besoin financier ou une difficulté réelle d'accès aux ressources nécessaires au développement, à la consolidation ou à la pérennité de son projet ou de son organisation",
  "Présenter un projet, une activité ou un besoin compatible avec la mission et les fins de la Fondation HORA",
  "Démontrer de quelle manière le soutien demandé contribuera à développer son autonomie, ses capacités, la stabilité de ses activités ou son impact dans la collectivité",
  "Être disposé à participer, lorsque requis, aux activités éducatives, au diagnostic, au mentorat, aux cellules d'expertise ou au suivi proposés par la Fondation",
  "Fournir les renseignements et documents nécessaires à l'évaluation de la demande",
  "S'engager à utiliser toute somme accordée exclusivement aux fins approuvées par la Fondation",
];

const attentionParticuliere = [
  "Vivent une situation de vulnérabilité ou de précarité financière",
  "Proviennent de milieux économiquement défavorisés",
  "Rencontrent des barrières importantes dans l'accès au financement, à la formation, aux réseaux ou à l'expertise spécialisée",
  "Cherchent à créer, maintenir ou consolider une source durable d'autonomie économique",
  "Portent un projet ou dirigent une organisation produisant un impact positif dans leur communauté",
];

const criteresObnl = [
  "Être légalement constitué ou être porté par un organisme admissible lorsque le programme le permet",
  "Poursuivre une mission compatible avec les objectifs du programme concerné",
  "Démontrer un besoin de financement, de consolidation ou d'accès à une expertise spécialisée",
  "Démontrer les retombées attendues du soutien sur ses bénéficiaires ou sa communauté",
  "Présenter une gouvernance et une capacité minimale permettant d'administrer adéquatement les sommes accordées",
  "Accepter les mécanismes de reddition de comptes prévus par la Fondation",
];

const besoins = [
  { icone: GraduationCap, titre: "Formation", texte: "Frais de participation à une formation permettant au bénéficiaire de développer ses compétences." },
  { icone: Lightbulb, titre: "Expertise spécialisée", texte: "Accès à une expertise juridique, financière, fiscale, stratégique, technologique, opérationnelle ou organisationnelle autrement difficilement accessible." },
  { icone: TrendingUp, titre: "Consolidation et pérennité", texte: "Dépenses admissibles directement liées à un plan de consolidation ou de développement des capacités." },
  { icone: RefreshCw, titre: "Transformation", texte: "Certains coûts liés à l'amélioration des systèmes, de la gouvernance, des processus ou des capacités organisationnelles." },
  { icone: Banknote, titre: "Soutien financier direct", texte: "Lorsque le programme de bourse le prévoit et que cette aide est compatible avec les fins de la Fondation." },
];

const exclusions = [
  "Au remboursement de dettes personnelles sans lien avec le programme",
  "À des dépenses personnelles sans rapport avec les objectifs de la bourse",
  "Au versement de dividendes ou à l'enrichissement personnel des propriétaires ou dirigeants",
  "À des activités contraires à la loi",
  "À des dépenses engagées avant l'approbation de la demande, sauf autorisation expresse",
  "À des dépenses qui ne peuvent être raisonnablement rattachées au projet ou aux objectifs approuvés",
];

const facteurs = ["Niveau de besoin", "Adéquation avec la mission", "Potentiel d'autonomie ou de consolidation", "Impact attendu", "Faisabilité du projet"];

const parcours = ["Éducation", "Expertise", "Autonomie", "Résilience"];

export default function Bourses() {
  const [campagne, setCampagne] = useState(null);

  useEffect(() => {
    getCampagneBourse().then(setCampagne).catch(() => setCampagne(null));
  }, []);

  return (
    <>
      <section className="bg-espresso py-28 text-ivory md:py-36" data-testid="bourses-hero">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">Bourses · Lever les barrières</p>
            <h1 className="mt-6 max-w-3xl font-serif text-5xl font-medium leading-[1.1] tracking-tight md:text-6xl" data-testid="bourses-title">
              <span className="italic text-gold">Bourse Impact</span>{" "}
              <span className="text-primary">par la Fondation HORA</span>
            </h1>
            <p className="mt-8 max-w-3xl text-base leading-relaxed text-ivory/70 md:text-lg" data-testid="bourses-intro">
              La Bourse Impact, offerte par la Fondation HORA, vise à favoriser l'autonomie économique, l'égalité des
              chances et la pérennité des initiatives en facilitant l'accès à des ressources
              financières, éducatives et spécialisées pour des personnes et des organisations qui
              rencontrent des obstacles limitant leur capacité à développer ou consolider leurs
              activités.
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-background py-24 md:py-32" data-testid="bourses-principe">
        <div className="mx-auto max-w-4xl px-6">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">Le principe</p>
            <blockquote className="mt-8 border-l-4 border-gold pl-8 font-serif text-2xl font-medium italic leading-snug text-foreground/90 md:text-3xl" data-testid="bourses-principe-texte">
              Il existe un potentiel de développement ou d'impact, mais une barrière financière ou
              d'accès aux ressources empêche le bénéficiaire de franchir une étape déterminante.
              La Fondation HORA contribue à lever cette barrière.
            </blockquote>
          </Reveal>
        </div>
      </section>

      <section className="border-t border-border bg-card py-24 md:py-28" data-testid="bourses-criteres">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">Critères d'admissibilité</p>
            <h2 className="mt-6 max-w-2xl font-serif text-4xl font-medium leading-[1.15] tracking-tight md:text-5xl">
              Personnes entrepreneures, OBNL et sociétés en démarrage
            </h2>
            <p className="mt-6 text-base text-foreground/60">Pour être admissible, le candidat doit généralement :</p>
          </Reveal>
          <div className="mt-12 grid gap-x-14 md:grid-cols-2">
            {criteresPersonnes.map((c, i) => (
              <Reveal key={i} delay={Math.min(i * 0.05, 0.3)}>
                <div className="flex gap-6 border-t border-border py-6" data-testid={`critere-${i}`}>
                  <span className="font-serif text-3xl font-medium text-gold">{String(i + 1).padStart(2, "0")}</span>
                  <p className="text-sm leading-relaxed text-foreground/70 md:text-base">{c}</p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal className="mt-14">
            <div className="border border-gold/40 bg-gold/10 p-10" data-testid="bourses-attention">
              <h3 className="font-serif text-2xl font-semibold md:text-3xl">Une attention particulière</h3>
              <p className="mt-4 text-sm leading-relaxed text-foreground/70 md:text-base">
                Conformément à sa mission d'inclusion économique et d'égalité des chances, la
                Fondation pourra accorder une attention particulière aux candidatures de personnes
                qui :
              </p>
              <ul className="mt-6 space-y-3">
                {attentionParticuliere.map((a, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm leading-relaxed text-foreground/80 md:text-base">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden="true" />
                    {a}
                  </li>
                ))}
              </ul>
              <p className="mt-8 border-t border-gold/30 pt-6 text-sm leading-relaxed text-foreground/60">
                Cette formulation demeure volontairement ouverte : des programmes ciblant les
                jeunes, les femmes, les nouveaux arrivants ou d'autres groupes pourront être créés
                lorsque le financement reçu le permettra, sans limiter la Fondation à ces
                catégories.
              </p>
            </div>
          </Reveal>

          <Reveal className="mt-14">
            <h3 className="font-serif text-2xl font-semibold md:text-3xl" data-testid="bourses-obnl-titre">Pour les OBNL et sociétés par actions en démarrage</h3>
            <p className="mt-4 text-base text-foreground/60">Pour être admissible, la personne morale devrait notamment :</p>
            <div className="mt-8 grid gap-x-14 md:grid-cols-2">
              {criteresObnl.map((c, i) => (
                <div key={i} className="flex items-start gap-3 border-t border-border py-5 text-sm leading-relaxed text-foreground/70 md:text-base" data-testid={`critere-obnl-${i}`}>
                  <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden="true" />
                  {c}
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="bg-background py-24 md:py-28" data-testid="bourses-besoins">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">Quels besoins la bourse peut-elle soutenir?</p>
            <h2 className="mt-6 max-w-2xl font-serif text-4xl font-medium leading-[1.15] tracking-tight md:text-5xl">
              Bien plus qu'un chèque
            </h2>
            <p className="mt-6 max-w-2xl text-base leading-relaxed text-foreground/60">
              Selon le programme, le soutien pourrait couvrir :
            </p>
          </Reveal>
          <div className="mt-14 grid gap-px overflow-hidden border border-border bg-border md:grid-cols-2 lg:grid-cols-3">
            {besoins.map((b, i) => (
              <Reveal key={b.titre} delay={Math.min(i * 0.08, 0.3)} className="bg-background">
                <div className="h-full p-10 transition-colors duration-300 hover:bg-card" data-testid={`besoin-${i}`}>
                  <b.icone size={28} strokeWidth={1.5} className="text-primary" />
                  <h3 className="mt-6 font-serif text-2xl font-semibold">{b.titre}</h3>
                  <p className="mt-3 text-sm leading-relaxed text-foreground/60">{b.texte}</p>
                </div>
              </Reveal>
            ))}
            <Reveal delay={0.3} className="bg-espresso">
              <div className="flex h-full flex-col justify-center p-10 text-ivory" data-testid="besoins-parcours">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold">Notre modèle</p>
                <div className="mt-5 flex flex-wrap items-center gap-x-2 gap-y-3 font-serif text-xl font-medium">
                  {parcours.map((p, i) => (
                    <span key={p} className="flex items-center gap-2">
                      {i > 0 && <ArrowRight size={16} className="text-gold" aria-hidden="true" />}
                      <span className={i % 2 === 1 ? "italic text-gold" : ""}>{p}</span>
                    </span>
                  ))}
                </div>
                <p className="mt-5 text-sm leading-relaxed text-ivory/60">
                  Chaque bourse raccorde directement le soutien accordé à ce parcours.
                </p>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-espresso py-24 text-ivory md:py-28" data-testid="bourses-exclusions">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-gold">Transparence envers nos bailleurs de fonds</p>
            <h2 className="mt-6 max-w-2xl font-serif text-4xl font-medium leading-[1.15] tracking-tight md:text-5xl">
              Ce que la bourse ne finance pas
            </h2>
            <p className="mt-6 text-base text-ivory/60">La bourse ne devrait normalement pas servir :</p>
          </Reveal>
          <div className="mt-12 grid gap-x-14 md:grid-cols-2">
            {exclusions.map((e, i) => (
              <Reveal key={i} delay={Math.min(i * 0.05, 0.25)}>
                <div className="flex items-start gap-4 border-t border-espresso-border py-5 text-sm leading-relaxed text-ivory/75 md:text-base" data-testid={`exclusion-${i}`}>
                  <Ban size={18} strokeWidth={1.8} className="mt-1 shrink-0 text-primary" aria-hidden="true" />
                  {e}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-background py-24 md:py-28" data-testid="bourses-selection">
        <div className="mx-auto max-w-4xl px-6">
          <Reveal>
            <p className="text-xs font-semibold uppercase tracking-[0.25em] text-primary">Sélection des bénéficiaires</p>
            <blockquote className="mt-8 font-serif text-2xl font-medium leading-snug text-foreground/90 md:text-3xl" data-testid="bourses-selection-texte">
              L'admissibilité à la Bourse Impact ne garantit pas l'octroi d'un financement.
              Les demandes sont évaluées en fonction des critères du programme, du besoin démontré,
              des retombées attendues, de la capacité du soutien à produire un changement durable et
              des ressources financières disponibles.
            </blockquote>
            <p className="mt-10 text-sm font-semibold uppercase tracking-[0.15em] text-foreground/50">
              Les décisions tiennent notamment compte de cinq facteurs
            </p>
            <div className="mt-6 flex flex-wrap gap-3" data-testid="bourses-facteurs">
              {facteurs.map((f, i) => (
                <span key={f} className="rounded-sm border border-border bg-card px-5 py-2.5 text-sm font-semibold" data-testid={`facteur-${i}`}>
                  <span className="mr-2 font-serif text-gold">{i + 1}.</span>{f}
                </span>
              ))}
            </div>
          </Reveal>

          <Reveal className="mt-20">
            <div className="border border-border bg-espresso p-10 text-ivory md:p-14" data-testid="bourses-cta">
              {campagne?.est_ouverte ? (
                <>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold" data-testid="bourse-status-open">Campagne ouverte</p>
                  <h2 className="mt-4 font-serif text-3xl font-semibold tracking-tight md:text-4xl">
                    Soumettre une demande de bourse
                  </h2>
                  <p className="mt-4 max-w-2xl text-sm leading-relaxed text-ivory/70 md:text-base">
                    Ce formulaire est réservé à l'analyse des projets pendant une campagne de bourse.
                    Pour une simple question ou demande d'information, utilisez plutôt la page Contact.
                  </p>
                  <BourseApplication campagne={campagne} />
                </>
              ) : (
                <>
                  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gold" data-testid="bourse-status-closed">Demandes fermées</p>
                  <h2 className="mt-4 font-serif text-3xl font-semibold tracking-tight md:text-4xl">
                    Les campagnes ne sont pas ouvertes toute l'année
                  </h2>
                  <p className="mt-4 max-w-xl text-sm leading-relaxed text-ivory/70 md:text-base">
                    Les programmes de bourses sont annoncés uniquement lorsqu'une campagne est ouverte
                    et que le financement le permet. Pour une question ou une demande d'information,
                    utilisez la page Contact; elle demeure disponible en tout temps.
                  </p>
                  <Link
                    to="/contact?sujet=bourse"
                    data-testid="bourses-contact-button"
                    className="group mt-8 inline-flex items-center gap-3 rounded-sm bg-primary px-8 py-4 text-sm font-semibold text-primary-foreground transition-[background-color,transform] duration-200 hover:-translate-y-0.5 hover:bg-[hsl(22,83%,40%)]"
                  >
                    Poser une question sur les bourses
                    <ArrowRight size={16} className="transition-transform duration-200 group-hover:translate-x-1" />
                  </Link>
                </>
              )}
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
